import { useEffect, useMemo, useState } from 'react';
import { SkupremeModal } from '@components/Modal';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { BoldText, ColoredPill, MuteText, StyledCheckbox } from '@elements/index';
import { Alert } from 'antd';
import type { InventorySuggestedTransferItem } from 'schemas/inventorySuggestedTransfers';
import styled from 'styled-components';
import { MapPin, RectangleDashed } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from 'semantic-ui-react';
import { InventoryService, ToastMessage } from '@utility/index';

const ListWrap = styled.div`
  max-height: 50vh;
  overflow-y: auto;
`;

const StaleReason = styled.div`
  color: #a8071a;
  font-size: 12px;
  margin-top: 4px;
`;

type InvalidSelectionReason = {
  id: string;
  reason?: string;
  message?: string;
  currentStatus?: string | null;
};

type BulkSuggestedTransferConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  items: InventorySuggestedTransferItem[];
  warehouseId?: string;
  onDeselect: (suggestionId: string) => void;
  onCompleted: () => void;
  onRefreshSelection?: () => Promise<InventorySuggestedTransferItem[]>;
};

export function BulkSuggestedTransferConfirmModal({
  open,
  onClose,
  items,
  warehouseId,
  onDeselect,
  onCompleted,
  onRefreshSelection,
}: BulkSuggestedTransferConfirmModalProps): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [invalidById, setInvalidById] = useState<Record<string, InvalidSelectionReason>>(
    {},
  );

  useEffect(() => {
    if (!open) setInvalidById({});
  }, [open]);

  const invalidItems = useMemo(
    () => items.filter(item => invalidById[item._id]),
    [invalidById, items],
  );

  const markInvalidSelections = (errors: InvalidSelectionReason[] = []) => {
    setInvalidById(
      errors.reduce<Record<string, InvalidSelectionReason>>((acc, error) => {
        if (error.id) acc[error.id] = error;
        return acc;
      }, {}),
    );
  };

  const refreshVisibleSelections = async () => {
    if (!onRefreshSelection) return;
    const latestRows = await onRefreshSelection();
    const latestById = new Map(latestRows.map(row => [row._id, row]));
    const visibleInvalid = items.flatMap(item => {
      const latest = latestById.get(item._id);
      if (!latest) return [];
      if (!['pending', 'modified'].includes(latest.status)) {
        return [
          {
            id: item._id,
            reason: 'invalid_status',
            currentStatus: latest.status,
            message:
              'This transfer changed since it was selected. Uncheck it or refresh Unassigned.',
          },
        ];
      }
      // Destination may legitimately be empty: it is resolved at put-away when
      // the item has no picking location yet. Only quantity/inventory are required.
      if (!latest.suggestedQuantity || !latest.inventoryId) {
        return [
          {
            id: item._id,
            reason: 'missing_quantity',
            message:
              'This transfer is missing quantity or inventory context. Uncheck it or refresh Unassigned.',
          },
        ];
      }
      return [];
    });
    if (visibleInvalid.length > 0) {
      markInvalidSelections(visibleInvalid);
      throw new Error('Selected suggested transfers changed');
    }
  };

  const handleConfirm = async () => {
    if (items.length === 0 || !warehouseId) {
      if (!warehouseId) {
        ToastMessage.error('Warehouse is required to create a replenishment wave');
      }
      return;
    }
    setLoading(true);
    try {
      setInvalidById({});
      await refreshVisibleSelections();
      await InventoryService.createReplenishmentWave({
        warehouseId,
        waveKind: 'replenishment',
        transferLineIds: items.map(i => i._id),
      });
      ToastMessage.success('Transfer batch created');
      onCompleted();
      onClose();
    } catch (e) {
      const errors = e?.details?.errors;
      if (e?.code === 'WAVE_ADD_LINES_INVALID' && Array.isArray(errors)) {
        markInvalidSelections(errors);
        ToastMessage.error(
          e.error ||
            'Some selected transfers changed. Uncheck the highlighted rows and try again.',
        );
      } else if (e?.message === 'Selected suggested transfers changed') {
        ToastMessage.error(
          'Some selected transfers changed. Uncheck the highlighted rows and try again.',
        );
      } else {
        ToastMessage.apiError(e);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SkupremeModal open={open} onClose={onClose} title="Confirm transfer batch">
      <MuteText className="mb-3 d-block">
        Review the suggested transfers below. Uncheck any row to remove it from this
        transfer batch.
      </MuteText>
      {invalidItems.length > 0 ? (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message="Some selected transfers changed"
          description="Uncheck the highlighted rows, refresh Unassigned, or create the batch with only the remaining valid transfers."
        />
      ) : null}
      <ListWrap>
        <Table basic="very" compact="very">
          <TableHeader>
            <TableRow>
              <TableCell collapsing />
              <TableCell>Product</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Qty</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => {
              const hasDestination = !!item.destinationLocationId;
              const invalidReason = invalidById[item._id];
              return (
                <TableRow key={item._id} negative={!!invalidReason}>
                  <TableCell verticalAlign="middle">
                    <StyledCheckbox
                      label=" "
                      checked
                      onChange={(_, { checked }) => {
                        if (!checked) {
                          setInvalidById(prev => {
                            const next = { ...prev };
                            delete next[item._id];
                            return next;
                          });
                          onDeselect(item._id);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell verticalAlign="middle">
                    <div className="title">{item.productId?.p_title}</div>
                    <MuteText className="mr-2">SKU</MuteText>
                    <BoldText>{item.productId?.p_sku}</BoldText>
                  </TableCell>
                  <TableCell verticalAlign="middle">
                    <ColoredPill mini color={item.sourceLocationId?.zoneId?.color}>
                      <RectangleDashed /> {item.sourceLocationId?.zoneId?.code}
                    </ColoredPill>
                    <ColoredPill
                      mini
                      className="mt-1"
                      color={item.sourceLocationId?.zoneId?.color}
                    >
                      <MapPin />
                      {item.sourceLocationId?.code}
                    </ColoredPill>
                  </TableCell>
                  <TableCell verticalAlign="middle">
                    {hasDestination ? (
                      <>
                        <ColoredPill
                          mini
                          color={item.destinationLocationId?.zoneId?.color}
                        >
                          <RectangleDashed /> {item.destinationLocationId?.zoneId?.code}
                        </ColoredPill>
                        <ColoredPill
                          mini
                          className="mt-1"
                          color={item.destinationLocationId?.zoneId?.color}
                        >
                          <MapPin />
                          {item.destinationLocationId?.code}
                        </ColoredPill>
                      </>
                    ) : (
                      <MuteText>No location assigned — scan open location</MuteText>
                    )}
                  </TableCell>
                  <TableCell verticalAlign="middle">
                    {item.suggestedQuantity} <MuteText>Units</MuteText>
                    {invalidReason ? (
                      <StaleReason>
                        {invalidReason.message ||
                          'This transfer is no longer available for batch creation.'}
                      </StaleReason>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListWrap>
      <div className="d-flex justify-content-end mt-3">
        <HoverBorderButton disabled={loading} onClick={onClose}>
          Cancel
        </HoverBorderButton>
        <StyledButton
          className="ml-2"
          loading={loading}
          disabled={items.length === 0 || loading || !warehouseId}
          onClick={handleConfirm}
        >
          Confirm
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
